// Column names match QuickBooks Online's invoice import sample
// (Settings → Import data → Invoices). Asterisks mark required fields.
export const QUICKBOOKS_INVOICE_HEADERS = [
  "*InvoiceNo",
  "*Customer",
  "*InvoiceDate",
  "*DueDate",
  "Terms",
  "Location",
  "Memo",
  "*Item(Product/Service)",
  "ItemDescription",
  "ItemQuantity",
  "ItemRate",
  "*ItemAmount",
  "Service Date",
];

const INVOICE_NUMBER_MAX_LENGTH = 21;
const NET_DAYS = 30;

const CATEGORY_ITEMS = {
  Material: "Materials",
  Materials: "Materials",
  Freight: "Freight",
  Labor: "Labor",
};

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function parseNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (value == null || value === "") {
    return 0;
  }
  const parsed = parseFloat(String(value).replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatMoney(value) {
  return roundMoney(value).toFixed(2);
}

function formatQuantity(value) {
  const rounded = Math.round(value * 100000) / 100000;
  return String(rounded);
}

function parseDateOnly(value) {
  if (!value) {
    return null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function formatDate(date) {
  if (!date) {
    return "";
  }
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}/${day}/${date.getFullYear()}`;
}

function addDays(date, days) {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

function csvEscape(value) {
  const text = value == null ? "" : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function contactName(contact) {
  if (!contact || typeof contact !== "object") {
    return "";
  }
  return `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
}

export function quickbooksCustomerName(proposal, project) {
  const accountName = project?.account?.name?.trim();
  if (accountName) {
    return accountName;
  }
  return contactName(proposal?.customer);
}

function formatAddress(address) {
  if (!address || typeof address !== "object") {
    return "";
  }
  const street = [address.line_1, address.line_2].filter(Boolean).join(" ");
  const region = [address.state, address.zip].filter(Boolean).join(" ");
  const cityLine = [address.city, region].filter(Boolean).join(", ");
  return [street, cityLine].filter(Boolean).join(", ");
}

function buildMemo(proposal, project) {
  const parts = [];
  if (project?.custom_id) {
    parts.push(`Job ${project.custom_id}`);
  }
  if (project?.name) {
    parts.push(project.name);
  }
  if (proposal?.name) {
    parts.push(proposal.name);
  }
  const person = contactName(proposal?.customer);
  if (person) {
    parts.push(person);
  }
  const address = formatAddress(proposal?.location?.address);
  if (address) {
    parts.push(address);
  }
  return parts.join(" | ");
}

export function quickbooksInvoiceNumber(proposal) {
  const cleaned = String(proposal?.name || "Proposal")
    .replace(/[\r\n,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return (cleaned || "Proposal").slice(0, INVOICE_NUMBER_MAX_LENGTH);
}

export function quickbooksInvoiceFilename(proposal) {
  const base = String(proposal?.name || "proposal")
    .trim()
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return `${base || "proposal"}-quickbooks.csv`;
}

function productNameForCategory(category) {
  return CATEGORY_ITEMS[category] || "Flooring";
}

function quantityAndRate(quantity, unitPrice, amount) {
  let qty = parseNumber(quantity);
  let rate = roundMoney(parseNumber(unitPrice));
  if (qty <= 0) {
    qty = 1;
  }
  if (rate <= 0) {
    rate = roundMoney(amount / qty);
  }
  if (Math.abs(roundMoney(qty * rate) - amount) > 0.01) {
    return { qty: 1, rate: amount };
  }
  return { qty, rate };
}

function moneyLine(item, description, amount) {
  const rounded = roundMoney(amount);
  return {
    item,
    description,
    qty: 1,
    rate: rounded,
    amount: rounded,
  };
}

function scaleLinesToTotal(lines, target) {
  const targetCents = Math.round(roundMoney(target) * 100);
  if (targetCents <= 0 || !lines.length) {
    return [];
  }
  const weights = lines.map((line) => Math.max(0, Math.round(line.amount * 100)));
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  if (weightSum <= 0) {
    return [];
  }
  const raw = weights.map((weight) => (weight * targetCents) / weightSum);
  const floors = raw.map((value) => Math.floor(value));
  let remainder = targetCents - floors.reduce((sum, value) => sum + value, 0);
  const byFraction = raw
    .map((value, index) => ({ index, fraction: value - floors[index] }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  const cents = [...floors];
  for (let i = 0; i < remainder; i += 1) {
    cents[byFraction[i % byFraction.length].index] += 1;
  }
  return lines
    .map((line, index) => {
      const amount = cents[index] / 100;
      return { ...line, qty: 1, rate: amount, amount };
    })
    .filter((line) => line.amount > 0);
}

function quoteSellLines(proposal, projectName) {
  const materialBase = parseNumber(proposal.material_subtotal);
  const freightBase = parseNumber(proposal.freight_subtotal);
  const markup = parseNumber(proposal.material_markup_rate) / 100;
  const taxRate = parseNumber(proposal.material_sales_tax_rate) / 100;
  const laborBase = parseNumber(proposal.labor_subtotal);
  const laborMarkup = parseNumber(proposal.labor_markup_rate) / 100;
  const consultantRate = parseNumber(proposal.consultant_fee_rate) / 100;
  const bond = roundMoney(parseNumber(proposal.p_and_p_bond));

  const materials = roundMoney(materialBase * (1 + markup));
  const freight = roundMoney(freightBase * (1 + markup));
  const salesTax = roundMoney(
    (materialBase + freightBase) * (1 + markup) * taxRate,
  );
  const labor = roundMoney(laborBase * (1 + laborMarkup));
  const subtotal = roundMoney(materials + freight + salesTax + labor);
  const consultantFee = roundMoney(subtotal * consultantRate);
  const job = projectName || "this project";

  return [
    moneyLine("Materials", `Materials for ${job}`, materials),
    moneyLine("Freight", `Freight for ${job}`, freight),
    moneyLine("Labor", `Labor for ${job}`, labor),
    moneyLine("Consultant Fee", `Consultant fee for ${job}`, consultantFee),
    moneyLine("Sales Tax", `Sales tax for ${job}`, salesTax),
    moneyLine("Performance Bond", "Payment and performance bond", bond),
  ].filter((line) => line.amount > 0);
}

function linesForProposalPrice(proposal, projectName) {
  const target = roundMoney(parseNumber(proposal.proposal_total));
  if (target <= 0) {
    throw new Error("Enter a proposal price before exporting to QuickBooks.");
  }

  const sellLines = quoteSellLines(proposal, projectName);
  if (!sellLines.length) {
    return [
      moneyLine(
        "Flooring",
        `Flooring for ${projectName || "this project"}`,
        target,
      ),
    ];
  }

  const fixedItems = new Set(["Sales Tax", "Performance Bond"]);
  const fixedLines = sellLines.filter((line) => fixedItems.has(line.item));
  const flexibleLines = sellLines.filter((line) => !fixedItems.has(line.item));
  const fixedSum = roundMoney(
    fixedLines.reduce((sum, line) => sum + line.amount, 0),
  );

  if (fixedSum > target) {
    return scaleLinesToTotal(sellLines, target);
  }

  const flexibleTarget = roundMoney(target - fixedSum);
  let allocatedFlexible = [];
  if (flexibleTarget > 0) {
    allocatedFlexible = flexibleLines.length
      ? scaleLinesToTotal(flexibleLines, flexibleTarget)
      : [
          moneyLine(
            "Flooring",
            `Flooring for ${projectName || "this project"}`,
            flexibleTarget,
          ),
        ];
  }
  return [...allocatedFlexible, ...fixedLines];
}

function itemizedLines(proposal, details, projectName) {
  const lines = [];
  (details?.inclusions || []).forEach((inclusion) => {
    const amount = roundMoney(parseNumber(inclusion.extension));
    if (amount <= 0) {
      return;
    }
    const item = productNameForCategory(inclusion.category);
    const { qty, rate } = quantityAndRate(
      inclusion.quantity,
      inclusion.unit_price,
      amount,
    );
    const description = String(inclusion.description || "").trim();
    lines.push({
      item,
      description: description || `${item} for ${projectName || "this project"}`,
      qty,
      rate,
      amount,
    });
  });

  if (!lines.length) {
    return [];
  }

  if (details?.include_sales_tax) {
    const taxable = lines
      .filter((line) => line.item === "Materials" || line.item === "Freight")
      .reduce((sum, line) => sum + line.amount, 0);
    const tax = roundMoney(
      taxable * (parseNumber(proposal.material_sales_tax_rate) / 100),
    );
    if (tax > 0) {
      lines.push(
        moneyLine(
          "Sales Tax",
          `Sales tax for ${projectName || "this project"}`,
          tax,
        ),
      );
    }
  }

  const bond = roundMoney(parseNumber(proposal.p_and_p_bond));
  if (bond > 0) {
    lines.push(
      moneyLine("Performance Bond", "Payment and performance bond", bond),
    );
  }
  return lines;
}

export function buildQuickbooksInvoiceRows(
  proposal,
  details,
  project,
  options = {},
) {
  const customer = quickbooksCustomerName(proposal, project);
  if (!customer) {
    throw new Error(
      "Add a customer or account before exporting to QuickBooks.",
    );
  }

  const projectName = project?.name?.trim() || "";
  const lines = details?.is_itemized
    ? itemizedLines(proposal, details, projectName)
    : [];
  const invoiceLines = lines.length
    ? lines
    : linesForProposalPrice(proposal, projectName);

  if (!invoiceLines.length) {
    throw new Error("This proposal has no amounts to export.");
  }

  const invoiceDate = parseDateOnly(options.invoiceDate) || parseDateOnly(new Date());
  const dueDate = addDays(invoiceDate, NET_DAYS);
  const serviceDate = formatDate(parseDateOnly(proposal?.start_date));
  const shared = {
    invoiceNo: quickbooksInvoiceNumber(proposal),
    customer,
    invoiceDate: formatDate(invoiceDate),
    dueDate: formatDate(dueDate),
    terms: "Net 30",
    location: "",
    memo: buildMemo(proposal, project),
    serviceDate,
  };

  return invoiceLines.map((line) => ({
    ...shared,
    item: line.item,
    description: line.description,
    quantity: formatQuantity(line.qty),
    rate: formatMoney(line.rate),
    amount: formatMoney(line.amount),
  }));
}

export function buildQuickbooksInvoiceCsv(
  proposal,
  details,
  project,
  options = {},
) {
  const rows = buildQuickbooksInvoiceRows(proposal, details, project, options);
  const body = rows.map((row) =>
    [
      row.invoiceNo,
      row.customer,
      row.invoiceDate,
      row.dueDate,
      row.terms,
      row.location,
      row.memo,
      row.item,
      row.description,
      row.quantity,
      row.rate,
      row.amount,
      row.serviceDate,
    ]
      .map(csvEscape)
      .join(","),
  );
  return [QUICKBOOKS_INVOICE_HEADERS.join(","), ...body].join("\r\n");
}

export function downloadCsvFile(filename, csvText) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
